import type { Building } from "building-types/building/buildingTypes";
import {
  ArrayEmitter,
  BuildingGeometryMode,
  type BuildingGeometry,
  type BuildingGeometryEmitter,
  type BuildingGeometryOptions,
  type NonBuildingViewerGeometryOptions,
} from "building-types/geometry/buildingGeometryTypes";
import type { FaceIterator } from "building-types/geometry/faceIteratorTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import {
  FaceCategory,
  type FaceGroupName,
  propFaceCategories,
  siteFaceCategories,
} from "building-types/materials/faceCategories";
import type { HomeSettings } from "building-types/settings/homeSettings";
import { assertIsNotNull } from "common/utils/assertOfType";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import { faceCategoryFromPropCategory } from "doc/propTools";
import { geomToFaces } from "geom/geom";
import { BUILDING_INTERIOR_LAYER, CLIPPING_LAYER } from "gfx/layerConstants";
import { createReportingUnitResolvingEmitter } from "../gltf/createReportingUnitResolvingEmitter";
import { resolveBuildingGeometryEntityId } from "./buildingGeometryEntityId";
import { emitBuildingProp } from "./buildingGeometryTools";
import { getCaseworkGeometries } from "./caseworkGeometry";
import { getCeilingGeometries } from "./ceilingGeometry";
import { getColumnGeometries } from "./columnGeometry";
import { getDoorGeometries } from "./doorGeometry";
import { getCornerBoardGeometries } from "./exteriorGeometry";
import { getFloorFinishGeometries } from "./floorFinishGeometry";
import { getFloorFramingGeometries } from "./floorFramingGeometry";
import { getSlabFlooringGeometries, getSubFlooringGeometries } from "./flooringGeometry";
import {
  getFoundationCurbGeometries,
  getFoundationFootingGeometries,
  getFoundationPierGeometries,
  getFoundationSlabGeometries,
  getFoundationStemWallGeometries,
} from "./foundationGeometry";
import { getGroundGeometries } from "./groundGeometry";
import { wrapWithNonFiniteDetection } from "./nonFiniteGeometryDetector";
import { getPorchGeometries } from "./porchGeometry";
import { getPropBoundingBoxesGeometries } from "./propsGeometry";
import { getRailingGeometries } from "./railingGeometry";
import { getRoofGeometries } from "./roofGeometry";
import { getSiteGeometries } from "./siteGeometry";
import { getStairSystemGeometries } from "./stairSystemGeometry";
import {
  getBaseTrimMoldingGeometries,
  getCrownTrimMoldingGeometries,
  getInteriorWallPlaneTrimMoldingGeometries,
} from "./trimGeometry";
import { getWainscotingGeometry, getWainscotingTrimGeometry } from "./wainscotingGeometry";
import { getWallFramingGeometries, getWallGeometries, WallGeometryFilter } from "./wallsGeometry";
import { getWindowGeometries, getWindowShuttersGeometries } from "./windowGeometry";

interface BuildingGeometryStage {
  readonly name: string;
  readonly run: () => void;
}

type BuildingGeometryYield = (stageName: string) => void | Promise<void>;

function addStage(stages: BuildingGeometryStage[], name: string, run: () => void): void {
  stages.push({ name, run });
}

function addGroupedStage(
  stages: BuildingGeometryStage[],
  emitter: BuildingGeometryEmitter,
  name: FaceGroupName,
  run: () => void,
): void {
  addStage(stages, name, () => {
    emitter.beginGroup?.(name);
    run();
    emitter.endGroup?.(name);
  });
}

function createBuildingGeometryStages(
  building: Building,
  settings: HomeSettings,
  entityTypes: Set<FaceCategory>,
  options: BuildingGeometryOptions,
  emitter: BuildingGeometryEmitter,
): BuildingGeometryStage[] {
  const stages: BuildingGeometryStage[] = [];

  if (entityTypes.has(FaceCategory.GROUND)) {
    addStage(stages, "Ground", () => {
      getGroundGeometries(building, emitter);
    });
  }

  if (
    options.mode !== BuildingGeometryMode.LINE_RENDER &&
    siteFaceCategories.some((faceCategory) => entityTypes.has(faceCategory))
  ) {
    addStage(stages, "Site", () => {
      getSiteGeometries(building, entityTypes, emitter);
    });
  }

  addGroupedStage(stages, emitter, "Roofs", () => {
    getRoofGeometries(building, settings, options, entityTypes, emitter);
  });

  if (entityTypes.has(FaceCategory.RAILING)) {
    addGroupedStage(stages, emitter, "Railings", () => {
      getRailingGeometries(building.railings, building.levelExtrusions, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.STAIR)) {
    addGroupedStage(stages, emitter, "Stairs", () => {
      getStairSystemGeometries(
        building.stairSystems,
        building.levelExtrusions,
        building.levels,
        building.exteriorPalette,
        emitter,
      );
    });
  }

  if (entityTypes.has(FaceCategory.ROOM_WALL_EXTERIOR) || entityTypes.has(FaceCategory.ROOM_WALL_INTERIOR)) {
    addGroupedStage(stages, emitter, "Walls", () => {
      getWallGeometries(building, settings, options, entityTypes, emitter, WallGeometryFilter.WALL_FINISH_ONLY);
    });
  }

  if (entityTypes.has(FaceCategory.ROOM_WALL_BREP) || entityTypes.has(FaceCategory.ROOM_WALL_EXTRUSION)) {
    addGroupedStage(stages, emitter, "Exterior Trim", () => {
      getWallGeometries(building, settings, options, entityTypes, emitter, WallGeometryFilter.WALL_SWEEP_ONLY);
    });
  }

  if (entityTypes.has(FaceCategory.WAINSCOTING) || entityTypes.has(FaceCategory.WAINSCOTING_TRIM)) {
    addGroupedStage(stages, emitter, "Wainscoting", () => {
      if (entityTypes.has(FaceCategory.WAINSCOTING)) {
        getWainscotingGeometry(building, emitter);
      }
      if (entityTypes.has(FaceCategory.WAINSCOTING_TRIM)) {
        getWainscotingTrimGeometry(building, emitter);
      }
    });
  }

  if (
    entityTypes.has(FaceCategory.FOUNDATION_GARAGE_CURB) ||
    entityTypes.has(FaceCategory.FOUNDATION_FOOTING) ||
    entityTypes.has(FaceCategory.FOUNDATION_PIER) ||
    entityTypes.has(FaceCategory.FOUNDATION_SLAB) ||
    entityTypes.has(FaceCategory.FOUNDATION_STEM_WALL)
  ) {
    addGroupedStage(stages, emitter, "Foundation", () => {
      if (entityTypes.has(FaceCategory.FOUNDATION_GARAGE_CURB)) {
        getFoundationCurbGeometries(building, emitter);
      }
      if (entityTypes.has(FaceCategory.FOUNDATION_FOOTING)) {
        getFoundationFootingGeometries(building, emitter);
      }
      if (entityTypes.has(FaceCategory.FOUNDATION_PIER)) {
        getFoundationPierGeometries(building, emitter);
      }
      if (entityTypes.has(FaceCategory.FOUNDATION_SLAB)) {
        getFoundationSlabGeometries(building, emitter);
      }
      if (entityTypes.has(FaceCategory.FOUNDATION_STEM_WALL)) {
        getFoundationStemWallGeometries(building, emitter);
      }
    });
  }

  if (
    entityTypes.has(FaceCategory.WINDOW_DECORATION) ||
    entityTypes.has(FaceCategory.WINDOW_EXTERIOR) ||
    entityTypes.has(FaceCategory.WINDOW_INTERIOR) ||
    entityTypes.has(FaceCategory.WINDOW_GLASS) ||
    entityTypes.has(FaceCategory.WINDOW_GRID) ||
    entityTypes.has(FaceCategory.WINDOW_SCREEN)
  ) {
    addGroupedStage(stages, emitter, "Windows", () => {
      getWindowGeometries(building, entityTypes, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.WINDOW_DECORATION)) {
    addGroupedStage(stages, emitter, "Windows Shutters", () => {
      getWindowShuttersGeometries(building, emitter);
    });
  }

  if (
    entityTypes.has(FaceCategory.DOOR_EXTERIOR) ||
    entityTypes.has(FaceCategory.DOOR_INTERIOR) ||
    entityTypes.has(FaceCategory.DOOR_CURVED_WALL_EXTERIOR) ||
    entityTypes.has(FaceCategory.DOOR_CURVED_WALL_INTERIOR) ||
    entityTypes.has(FaceCategory.DOOR_DECORATION)
  ) {
    addGroupedStage(stages, emitter, "Doors", () => {
      getDoorGeometries(building, entityTypes, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.CASEWORK)) {
    addGroupedStage(stages, emitter, "Casework", () => {
      getCaseworkGeometries(building, options.mode, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.PORCH_DECORATION)) {
    addGroupedStage(stages, emitter, "Porches", () => {
      getPorchGeometries(building.porches, building.exteriorPalette, emitter, building.settings.props);
    });
  }

  if (entityTypes.has(FaceCategory.CORNER_BOARD)) {
    addGroupedStage(stages, emitter, "Exterior Trim", () => {
      getCornerBoardGeometries(building, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.FLOOR) || entityTypes.has(FaceCategory.PORCH_EXTERIOR)) {
    addGroupedStage(stages, emitter, "Floors", () => {
      getFloorFinishGeometries(building, options, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.FLOOR_FRAMING)) {
    addGroupedStage(stages, emitter, "Floor Framing", () => {
      getFloorFramingGeometries(
        building.floorSystems,
        building.framedFloorAssemblies,
        building.rooms,
        building.roomsByLevel,
        emitter,
      );
    });
  }

  if (entityTypes.has(FaceCategory.SLAB_FLOORING)) {
    addGroupedStage(stages, emitter, "Slab Flooring", () => {
      getSlabFlooringGeometries(
        building.floorSystems,
        building.slabFloorAssemblies,
        building.rooms,
        building.roomsByLevel,
        emitter,
        building.doc.floors,
      );
    });
  }

  if (entityTypes.has(FaceCategory.SUB_FLOORING)) {
    addGroupedStage(stages, emitter, "Sub Flooring", () => {
      getSubFlooringGeometries(
        building.floorSystems,
        building.framedFloorAssemblies,
        building.slabFloorAssemblies,
        building.rooms,
        building.roomsByLevel,
        emitter,
        building.doc.floors,
      );
    });
  }

  if (entityTypes.has(FaceCategory.BASE_TRIM_MOLDING)) {
    addGroupedStage(stages, emitter, "Base Trim Molding", () => {
      getBaseTrimMoldingGeometries(building, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.CROWN_TRIM_MOLDING)) {
    addGroupedStage(stages, emitter, "Crown Trim Molding", () => {
      getCrownTrimMoldingGeometries(building, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.WALL_PLANE_TRIM_MOLDING)) {
    addGroupedStage(stages, emitter, "Interior Wall Plane Trim Molding", () => {
      getInteriorWallPlaneTrimMoldingGeometries(building, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.COLUMN)) {
    addGroupedStage(stages, emitter, "Columns", () => {
      getColumnGeometries(building.columns, building.rooms, building.exteriorPalette, emitter);
    });
  }

  if (entityTypes.has(FaceCategory.WALL_FRAMING)) {
    addGroupedStage(stages, emitter, "Wall Framing", () => {
      getWallFramingGeometries({
        wallFramingSystems: building.wallFramingSystems,
        faceCategory: FaceCategory.WALL_FRAMING,
        debugColors: false,
        emitter,
      });
    });
  }

  if (entityTypes.has(FaceCategory.GABLE_END_WALL_FRAMING)) {
    addGroupedStage(stages, emitter, "Gable End Wall Framing", () => {
      getWallFramingGeometries({
        wallFramingSystems: building.gableEndWallFramingSystems,
        faceCategory: FaceCategory.GABLE_END_WALL_FRAMING,
        debugColors: false,
        emitter,
      });
    });
  }

  if (entityTypes.has(FaceCategory.CEILING)) {
    addGroupedStage(stages, emitter, "Ceilings", () => {
      getCeilingGeometries(building, emitter);
    });
  }

  if (propFaceCategories.some((fc) => entityTypes.has(fc))) {
    addStage(stages, "Props", () => {
      const propCatalog = building.settings.props;

      for (const propId in building.props) {
        const prop = building.props[propId];
        assertIsNotNull(prop.room);

        const propAttributes = propCatalog[prop.src];
        if (!propAttributes) {
          continue;
        }

        const faceCategory = faceCategoryFromPropCategory(propAttributes.category);
        if (!entityTypes.has(faceCategory)) {
          continue;
        }

        const levelId = prop.room.level.id;
        const threeLayers = [BUILDING_INTERIOR_LAYER, CLIPPING_LAYER];
        const entityHandleForSelectingReportingUnit = roomEntityHandleForSelectingReportingUnit(prop.room);
        emitBuildingProp(emitter, prop, {
          threeLayers,
          faceCategory,
          propCatalog,
          levelId,
          entityHandleForSelectingReportingUnit,
        });
      }
    });
  }

  return stages;
}

/**
 * Generate BuildingGeometry for a building and emit to the provided emitter.
 * @param building The building to generate geometry for
 * @param settings Home settings
 * @param entityTypes Set of face categories to include
 * @param options Geometry generation options
 * @param emitter Optional emitter to receive geometry (defaults to ArrayEmitter)
 */
export function getBuildingGeometries(
  building: Building,
  settings: HomeSettings,
  entityTypes: Set<FaceCategory>,
  options: BuildingGeometryOptions,
  emitter: BuildingGeometryEmitter = new ArrayEmitter(),
): void {
  const { emitter: geometryEmitter, report } = wrapWithNonFiniteDetection(emitter, building);
  for (const stage of createBuildingGeometryStages(building, settings, entityTypes, options, geometryEmitter)) {
    stage.run();
  }
  report();
}

/**
 * Async variant of getBuildingGeometries that can yield between top-level geometry stages.
 */
export async function getBuildingGeometriesAsync(
  building: Building,
  settings: HomeSettings,
  entityTypes: Set<FaceCategory>,
  options: BuildingGeometryOptions,
  emitter: BuildingGeometryEmitter = new ArrayEmitter(),
  yieldBetweenStages?: BuildingGeometryYield,
): Promise<void> {
  const { emitter: geometryEmitter, report } = wrapWithNonFiniteDetection(emitter, building);
  for (const stage of createBuildingGeometryStages(building, settings, entityTypes, options, geometryEmitter)) {
    stage.run();
    await yieldBetweenStages?.(stage.name);
  }
  report();
}

function iterateBuildingGeometriesInternal(
  buildingGeometries: BuildingGeometry[],
  options: NonBuildingViewerGeometryOptions,
  iterateFace: FaceIterator,
) {
  const { addEntityIds = false, entityTable = undefined } = options;
  const addEntityIdToFaceArrays = addEntityIds && entityTable !== undefined;

  for (const buildingGeometry of buildingGeometries) {
    const {
      faceCategory,
      geom,
      materialSrc,
      materialSlot,
      uvGen,
      materialTransformation,
      generateNeighbors,
      entityHandleForSelectingReportingUnit,
      reportingUnitLabelId,
    } = buildingGeometry;

    const id = resolveBuildingGeometryEntityId({
      addEntityIdToFaceArrays,
      entityTable,
      buildingGeometry,
    });

    iterateFace({
      faces: geomToFaces(geom),
      materialTag: materialSlot,
      material: materialSrc,
      faceCategory,
      uvGen,
      materialTransformation,
      entityId: id,
      entityHandleForSelectingReportingUnit,
      reportingUnitLabelId,
      generateNeighbors,
    });
  }
}

export function iterateBuildingGeometries(
  building: Building,
  entityTypes: Set<FaceCategory>,
  options: NonBuildingViewerGeometryOptions,
  iterateFace: FaceIterator,
  resolveReportingUnits: boolean = false,
) {
  const emitter = new ArrayEmitter();
  // Only pay for per-geometry reporting-unit resolution when a consumer actually filters by unit
  // (e.g. a single townhouse unit thumbnail). Callers that ignore reportingUnitLabelId skip it.
  const targetEmitter = resolveReportingUnits ? createReportingUnitResolvingEmitter(building.doc, emitter) : emitter;
  getBuildingGeometries(building, building.settings, entityTypes, options, targetEmitter);

  iterateBuildingGeometriesInternal(emitter.geometries, options, iterateFace);
}

export async function iteratePropBoundingBoxes(
  building: Building,
  visibilitySelectors: SheetGeometrySelectorEffectMap[] | undefined,
  options: NonBuildingViewerGeometryOptions,
  iterateFace: FaceIterator,
): Promise<void> {
  const emitter = new ArrayEmitter();
  await getPropBoundingBoxesGeometries(building, building.doc.assets, visibilitySelectors, emitter);

  iterateBuildingGeometriesInternal(emitter.geometries, options, iterateFace);
}
